import { call, put, takeLatest } from 'redux-saga/effects';
import i18n from '../../../i18n';
import entryExitStatusService from '../../../services/entryExitStatusService';
import {
  fetchEntryExitStatuses,
  fetchEntryExitStatusesSuccess,
  fetchEntryExitStatusesFailure,
  fetchEntryExitStatusById,
  fetchEntryExitStatusByIdSuccess,
  fetchEntryExitStatusByIdFailure,
  addEntryExitStatus,
  addEntryExitStatusSuccess,
  addEntryExitStatusFailure,
  updateEntryExitStatus,
  updateEntryExitStatusSuccess,
  updateEntryExitStatusFailure,
  deleteEntryExitStatus,
  deleteEntryExitStatusSuccess,
  deleteEntryExitStatusFailure,
  bulkDeleteEntryExitStatuses,
  bulkDeleteEntryExitStatusesSuccess,
  bulkDeleteEntryExitStatusesFailure,
} from './slice';

/**
 * Translate error message from backend
 */
function translateError(errorMessage) {
  if (!errorMessage) return i18n.t('entryExitStatuses.errors.unknown');

  const errorMap = {
    'Entry/Exit status name already exists': 'entryExitStatuses.errors.nameExists',
    'Entry exit status not found': 'entryExitStatuses.errors.entryExitStatusNotFound',
    'Failed to add entry exit status': 'entryExitStatuses.errors.addFailed',
    'Failed to update entry exit status': 'entryExitStatuses.errors.updateFailed',
    'Failed to delete entry exit status': 'entryExitStatuses.errors.deleteFailed',
    'Failed to fetch entry exit statuses': 'entryExitStatuses.errors.fetchFailed',
  };

  // Check for constraint violation (status in use)
  if (errorMessage.includes('Cannot delete entry/exit status') && errorMessage.includes('being used by entry/exit reasons')) {
    // Return the detailed error message from backend (includes which reasons are using it)
    return errorMessage;
  }

  for (const [key, translationKey] of Object.entries(errorMap)) {
    if (errorMessage.includes(key)) {
      return i18n.t(translationKey);
    }
  }

  return errorMessage;
}

function* handleFetchEntryExitStatuses(action) {
  try {
    const response = yield call(entryExitStatusService.getEntryExitStatuses, action.payload);
    yield put(fetchEntryExitStatusesSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchEntryExitStatusesFailure(translatedError));
  }
}

function* handleFetchEntryExitStatusById(action) {
  try {
    const response = yield call(entryExitStatusService.getEntryExitStatusById, action.payload);
    yield put(fetchEntryExitStatusByIdSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(fetchEntryExitStatusByIdFailure(translatedError));
  }
}

function* handleAddEntryExitStatus(action) {
  try {
    const response = yield call(entryExitStatusService.createEntryExitStatus, action.payload);
    yield put(addEntryExitStatusSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(addEntryExitStatusFailure(translatedError));
  }
}

function* handleUpdateEntryExitStatus(action) {
  try {
    const response = yield call(entryExitStatusService.updateEntryExitStatus, action.payload);
    yield put(updateEntryExitStatusSuccess(response));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(updateEntryExitStatusFailure(translatedError));
  }
}

function* handleDeleteEntryExitStatus(action) {
  try {
    const entryExitStatusId = action.payload;
    yield call(entryExitStatusService.deleteEntryExitStatus, entryExitStatusId);
    yield put(deleteEntryExitStatusSuccess(entryExitStatusId));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(deleteEntryExitStatusFailure(translatedError));
  }
}

function* handleBulkDeleteEntryExitStatuses(action) {
  try {
    const entryExitStatusIds = action.payload;
    yield call(entryExitStatusService.bulkDeleteEntryExitStatuses, entryExitStatusIds);
    yield put(bulkDeleteEntryExitStatusesSuccess(entryExitStatusIds));
  } catch (error) {
    const translatedError = translateError(error.message);
    yield put(bulkDeleteEntryExitStatusesFailure(translatedError));
  }
}

export function* watchEntryExitStatusesActions() {
  yield takeLatest(fetchEntryExitStatuses.type, handleFetchEntryExitStatuses);
  yield takeLatest(fetchEntryExitStatusById.type, handleFetchEntryExitStatusById);
  yield takeLatest(addEntryExitStatus.type, handleAddEntryExitStatus);
  yield takeLatest(updateEntryExitStatus.type, handleUpdateEntryExitStatus);
  yield takeLatest(deleteEntryExitStatus.type, handleDeleteEntryExitStatus);
  yield takeLatest(bulkDeleteEntryExitStatuses.type, handleBulkDeleteEntryExitStatuses);
}

